/**
 * Shop-by-size collection sorter. Available variants float to the top,
 * sold-out drop to the bottom; original order persisted on
 * data-original-order so toggling the size off restores the natural sort.
 *
 * AI: Entirely written by AI
 * Date: 2026-05-05
 * Model: Claude Opus 4.7
 */

import { log } from './log.js';
import { appGet } from './app-get.js';
import { fadeIn, fadeOut, stop as stopAnim } from './core-anim.js';
import { fireChange, COLLECTION_SELECTOR } from './dom.js';

const SHOP_BY_SIZE_SELECTOR = 'select[name="footwear_size"]';
const RESET_SIZE_SELECTOR = '.js-reset-collection-size';
const UNAVAILABLE_CLASS = 'variant-is-unavailable';
const RESET_FADE_MS = 300;

function getCardSelector() {
  return appGet('dom.product.cardSelector', '.js-product-card:not(.grid-tout)');
}

function toggleResetButton(visible) {
  const button = document.querySelector(RESET_SIZE_SELECTOR);
  if (!button) return;
  stopAnim(button);
  if (visible) {
    fadeIn(button, { duration: RESET_FADE_MS, display: 'flex' });
  } else {
    fadeOut(button, { duration: RESET_FADE_MS });
  }
}

export function handleShopBySizeChange(select) {
  log('[Filter] Shop By Size changed', { value: select.value || '(cleared)' });
  const collection = document.querySelector(COLLECTION_SELECTOR);
  if (!collection) return;
  const sizeValue = select.value;
  const hasSize = sizeValue !== '';
  const cards = Array.from(collection.querySelectorAll(getCardSelector()));
  if (cards.length === 0) return;
  const cardParent = cards[0].parentElement;
  if (!cardParent) return;

  const radioSelector = hasSize
    ? `input[type="radio"][name="size"][value="${sizeValue}"]`
    : 'input[type="radio"][name="size"]';
  const radios = cards.flatMap((card) => Array.from(card.querySelectorAll(radioSelector)));
  const hasOriginalOrder = cards[0].dataset.originalOrder != null;

  for (const radio of radios) {
    radio.checked = hasSize;
    fireChange(radio);
  }

  // Wait one frame so radio change events propagate (variant lookup +
  // .variant-is-unavailable class toggling) before we reorder.
  if (hasSize) {
    requestAnimationFrame(() => {
      cards.forEach((card, index) => {
        if (!hasOriginalOrder) card.dataset.originalOrder = String(index);
        if (card.classList.contains(UNAVAILABLE_CLASS)) cardParent.appendChild(card);
      });
      // Build a fragment so the available cards land at the top of the
      // parent in their original-order sequence (one reflow vs N).
      const fragment = document.createDocumentFragment();
      cards
        .filter((card) => !card.classList.contains(UNAVAILABLE_CLASS))
        .sort((a, b) => Number(a.dataset.originalOrder) - Number(b.dataset.originalOrder))
        .forEach((card) => fragment.appendChild(card));
      cardParent.insertBefore(fragment, cardParent.firstChild);
    });
  } else {
    requestAnimationFrame(() => {
      cards
        .slice()
        .sort((a, b) => Number(a.dataset.originalOrder) - Number(b.dataset.originalOrder))
        .forEach((card) => cardParent.appendChild(card));
    });
  }

  toggleResetButton(hasSize);
}

export function clearSize() {
  const collection = document.querySelector(COLLECTION_SELECTOR);
  const select = collection && collection.querySelector(SHOP_BY_SIZE_SELECTOR);
  if (!select) return;
  // Reset to the placeholder (first option), then dispatch change so
  // handleShopBySizeChange runs the unsort path.
  select.value = select.options[0] ? select.options[0].value : '';
  fireChange(select);
}

log('[Filter] size sort registered');
